/**
 * ErrorScreen — the shell shared by 404 / 500 / any future error surface.
 *
 * Brand spec §13.1: Studio Slate, no gradients. Layout is restrained — brand
 * mark top-left, centered single-column content, support footer. Status code
 * is rendered with `.num` (mono + tabular) so it reads as data, not chrome.
 *
 * Kept dependency-light on purpose: this component must render even when the
 * tRPC client, query client, or router context is mid-failure. It pulls only
 * Button + cn from `@flint/ui` and Link from `@tanstack/react-router`.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@flint/ui";

export type ErrorTone = "sky" | "warn" | "bad";

export interface ErrorScreenProps {
  /** HTTP-style code displayed as the eyebrow signal. e.g. `404`, `500`. */
  status: number | string;
  /** Short uppercase eyebrow label following the status. */
  kicker: string;
  /** Tone of the eyebrow dot + status digits. */
  tone?: ErrorTone;
  /** The h1. */
  title: React.ReactNode;
  /** Subtitle paragraph. */
  description?: React.ReactNode;
  /** Action row — typically two `<Button>`s. */
  actions?: React.ReactNode;
  /** Optional below-the-fold detail (e.g. dev stack trace, request id). */
  detail?: React.ReactNode;
}

export function ErrorScreen({
  status,
  kicker,
  tone = "warn",
  title,
  description,
  actions,
  detail,
}: ErrorScreenProps) {
  const dotClass = tone === "sky" ? "bg-sky-600" : tone === "bad" ? "bg-bad" : "bg-warn";

  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col">
      <header className="px-6 py-4">
        <Link to="/" className="inline-flex items-center gap-2 group">
          <span aria-hidden className="inline-flex">
            <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
              <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="10" cy="10" r="2.5" fill="#0284C7" />
            </svg>
          </span>
          <span className="font-semibold text-ink group-hover:text-ink-2">Flint</span>
          <span className="text-muted text-xs">Flint</span>
        </Link>
      </header>

      <main role="main" className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md flex flex-col gap-6">
          <div className="flex items-baseline gap-3">
            <span aria-hidden className={cn("w-1.5 h-1.5 rounded-full self-center", dotClass)} />
            <span className="text-xs font-medium text-muted uppercase tracking-[0.08em]">
              <span className="num text-ink-2">{status}</span> · {kicker}
            </span>
          </div>

          <h1 className="text-2xl leading-[32px] font-semibold tracking-[-0.01em] text-ink">
            {title}
          </h1>

          {description ? (
            <p className="text-sm leading-[22px] text-muted max-w-prose">{description}</p>
          ) : null}

          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}

          {detail ? <div className="pt-2 border-t border-line">{detail}</div> : null}
        </div>
      </main>

      <footer className="px-6 py-4 text-xs leading-[17px] text-muted">
        Need a hand?{" "}
        <a
          href="mailto:support@flowtrail.in"
          className="text-sky-700 hover:underline focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2 rounded-sm"
        >
          support@flowtrail.in
        </a>
      </footer>
    </div>
  );
}
